/**
 * AtlasGuess audio synthesizer — Web Audio API ticks + dings, no asset
 * files. Tunings ported from the prototype's playTick / playDing /
 * playTravelAndDing (reference/prototype.html ~line 2497) so the in-game
 * feel matches what we know works.
 *
 * Design notes:
 *
 * 1. **No samples, no assets.** Every sound is generated on demand from
 *    OscillatorNode + GainNode. Deterministic, tiny bundle, easy to retune.
 *    Real audio samples can be swapped in later if a sound designer joins;
 *    the API stays the same (callers don't care about the source).
 *
 * 2. **Lazy AudioContext.** Browsers suspend audio until a user gesture.
 *    We lazy-init the context on first use and call .resume() each time —
 *    that "user gesture" requirement is met by the click that triggered
 *    the sound in the first place.
 *
 * 3. **Score-agnostic.** The audio package takes raw frequencies and
 *    durations. Game-mode-specific mappings (miles → tick rate, distance
 *    → ding pitch) live in the game mode, not here, so the same player
 *    serves city-distance modes, tap-the-country, and anything else.
 *
 * 4. **Cancellable sequences.** A multi-tick "travel" sequence can be
 *    cancelled mid-flight (user hits Next, round ends, etc.) — every
 *    scheduled oscillator is tracked so cancel() stops them cleanly.
 */

export interface AudioPlayer {
  /** Whether sounds will play. setEnabled(false) silences future calls. */
  setEnabled(enabled: boolean): void;
  isEnabled(): boolean;

  /** Single short tick — for tactile click feedback or one-off ticks. */
  tick(options?: TickOptions): void;

  /** Single ding — for completion / success / answer reveal. */
  ding(options?: DingOptions): void;

  /**
   * Schedule a sequence of ticks across `durationMs`, then a ding at the
   * end. Returns a handle whose `cancel()` stops every pending sound.
   * Used by city-distance modes to "travel" from guess to answer.
   */
  playSequence(options: SequenceOptions): SequenceHandle;

  /** Cancel any in-flight sequence (no-op if none active). */
  cancel(): void;

  /** Free the underlying AudioContext. Call on unmount. */
  dispose(): void;
}

export interface TickOptions {
  /** Hz. Default 1400 (matches prototype's travel tick). */
  freq?: number;
  /** 0-1 amplitude. Default 0.08. */
  gain?: number;
  /** Default 35ms. */
  durationMs?: number;
  /**
   * Track the scheduled oscillator so it can be cancelled by cancel().
   * Default false — single-tick clicks shouldn't be cancelled along with
   * a travel sequence.
   */
  trackForCancel?: boolean;
  /** Schedule offset from "now" in seconds. Default 0 (play immediately). */
  delaySec?: number;
}

export interface DingOptions {
  /**
   * Single freq or array for chord. Default 660 Hz (E5). Arrays of 2-3
   * frequencies give a fuller "achievement" sound; the partials are
   * detuned slightly so the chord feels organic, not electronic.
   */
  frequencies?: number | number[];
  /**
   * Peak gain per partial. Default 0.18 / sqrt(N) so chords stay
   * perceptually even with single-note dings.
   */
  gain?: number;
  /** Total tail length. Default 900ms. */
  durationMs?: number;
  /** Track for cancellation. Default false. */
  trackForCancel?: boolean;
  /** Schedule offset from "now" in seconds. Default 0. */
  delaySec?: number;
}

export interface SequenceOptions {
  /** Total tick playback time. Ding plays right after the last tick. */
  durationMs: number;
  /**
   * Frequencies for the final ding. Single freq or chord array. Game mode
   * computes this from its own scoring (e.g. higher pitch for closer guess).
   */
  dingFrequencies: number | number[];
  /** Ticks per second. Default 14 (matches prototype). */
  tickRate?: number;
  /** Minimum number of ticks even for very short durations. Default 3. */
  minTicks?: number;
  /**
   * Tick frequency — alternates ±120 Hz around this for organic feel.
   * Default 1300 (matches prototype).
   */
  tickBaseFreq?: number;
}

export interface SequenceHandle {
  /** Cancel every still-pending sound in this sequence. Idempotent. */
  cancel(): void;
  /** Resolves when the ding plays. Rejects with `'cancelled'` if cancelled first. */
  done: Promise<void>;
}

export interface CreateAudioOptions {
  /** Initial enabled state. Default true. */
  enabled?: boolean;
}

interface PlayerState {
  enabled: boolean;
  ctx: AudioContext | null;
  scheduledOscillators: OscillatorNode[];
}

/** Build an AudioPlayer. One per app session is plenty. */
export function createAudio(options: CreateAudioOptions = {}): AudioPlayer {
  const state: PlayerState = {
    enabled: options.enabled ?? true,
    ctx: null,
    scheduledOscillators: [],
  };

  // --- AudioContext lifecycle ---

  function getCtx(): AudioContext | null {
    if (state.ctx) return state.ctx;
    if (typeof window === 'undefined') return null;
    const Ctor =
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      console.warn('[atlas/audio] Web Audio API unavailable');
      return null;
    }
    try {
      state.ctx = new Ctor();
    } catch (err) {
      console.warn('[atlas/audio] AudioContext construction failed', err);
      return null;
    }
    return state.ctx;
  }

  /** Resume the context if suspended (browsers suspend until user gesture). */
  function ensureRunning(ctx: AudioContext): void {
    if (ctx.state === 'suspended') void ctx.resume();
  }

  // --- Primitive: schedule one oscillator with an attack/decay envelope ---

  function scheduleOsc(
    ctx: AudioContext,
    startTime: number,
    freq: number,
    peakGain: number,
    attackSec: number,
    decayEndSec: number,
    detuneCents = 0,
    track = false,
  ): OscillatorNode {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.detune.value = detuneCents;
    g.gain.setValueAtTime(0, startTime);
    g.gain.linearRampToValueAtTime(peakGain, startTime + attackSec);
    g.gain.exponentialRampToValueAtTime(0.0001, startTime + decayEndSec);
    osc.connect(g).connect(ctx.destination);
    osc.start(startTime);
    // Slight buffer past the decay end to avoid clipping on driver
    // implementations that round-down stop times.
    osc.stop(startTime + decayEndSec + 0.02);
    if (track) state.scheduledOscillators.push(osc);
    return osc;
  }

  // --- Public API ---

  function tick(opts: TickOptions = {}): void {
    if (!state.enabled) return;
    const ctx = getCtx();
    if (!ctx) return;
    ensureRunning(ctx);
    const freq = opts.freq ?? 1400;
    const gain = opts.gain ?? 0.08;
    const durSec = (opts.durationMs ?? 35) / 1000;
    const start = ctx.currentTime + (opts.delaySec ?? 0);
    scheduleOsc(ctx, start, freq, gain, 0.002, durSec, 0, opts.trackForCancel ?? false);
  }

  function ding(opts: DingOptions = {}): void {
    if (!state.enabled) return;
    const ctx = getCtx();
    if (!ctx) return;
    ensureRunning(ctx);
    const freqs = normalizeFrequencies(opts.frequencies ?? 660);
    const peakBase = opts.gain ?? 0.18 / Math.sqrt(freqs.length);
    const decaySec = (opts.durationMs ?? 900) / 1000;
    const start = ctx.currentTime + (opts.delaySec ?? 0);
    freqs.forEach((f, i) => {
      // Subtle detune across partials gives the chord a "richer, not
      // electronic" feel — straight unison tones sound synthesized.
      const detune = (i - (freqs.length - 1) / 2) * 2;
      scheduleOsc(ctx, start, f, peakBase, 0.008, decaySec, detune, opts.trackForCancel ?? false);
    });
  }

  function playSequence(opts: SequenceOptions): SequenceHandle {
    if (!state.enabled) {
      return { cancel: () => {}, done: Promise.resolve() };
    }
    const ctx = getCtx();
    if (!ctx) {
      return { cancel: () => {}, done: Promise.resolve() };
    }
    ensureRunning(ctx);

    const durationSec = opts.durationMs / 1000;
    const tickRate = opts.tickRate ?? 14;
    const minTicks = opts.minTicks ?? 3;
    const tickBaseFreq = opts.tickBaseFreq ?? 1300;

    // Min count guarantees even very-close guesses get a "bup-bup-ding"
    // rhythm rather than just an instantaneous ding.
    const tickCount = Math.max(minTicks, Math.round(durationSec * tickRate));
    const interval = durationSec / tickCount;

    let cancelled = false;
    let resolve!: () => void;
    let reject!: (reason: string) => void;
    const done = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    // Ticks: alternate ±120 Hz around base for organic feel.
    for (let i = 0; i < tickCount; i++) {
      const freq = tickBaseFreq + (i % 2) * 120;
      tick({
        freq,
        gain: 0.055,
        durationMs: 28,
        trackForCancel: true,
        delaySec: i * interval,
      });
    }
    // Ding right after the last tick. Tiny gap so the final tick's
    // exponential tail doesn't smear into the ding's attack.
    ding({
      frequencies: opts.dingFrequencies,
      trackForCancel: true,
      delaySec: durationSec + 0.02,
    });

    const dingTimer = setTimeout(
      () => {
        if (!cancelled) resolve();
      },
      // Resolve roughly at ding-start; callers usually only care about timing
      // the next round, not the full ding tail.
      Math.round(durationSec * 1000) + 20,
    );

    return {
      cancel: () => {
        if (cancelled) return;
        cancelled = true;
        clearTimeout(dingTimer);
        cancel();
        reject('cancelled');
      },
      done,
    };
  }

  function cancel(): void {
    const ctx = state.ctx;
    if (!ctx) return;
    for (const osc of state.scheduledOscillators) {
      try {
        osc.stop(ctx.currentTime);
      } catch {
        // Already stopped — fine.
      }
    }
    state.scheduledOscillators = [];
  }

  function setEnabled(enabled: boolean): void {
    state.enabled = enabled;
    if (!enabled) cancel();
  }

  function isEnabled(): boolean {
    return state.enabled;
  }

  function dispose(): void {
    cancel();
    if (state.ctx) {
      void state.ctx.close();
      state.ctx = null;
    }
  }

  return { tick, ding, playSequence, cancel, setEnabled, isEnabled, dispose };
}

function normalizeFrequencies(input: number | number[]): number[] {
  return Array.isArray(input) ? input : [input];
}
